import { decodeOrientedImage } from '../codecs/oriented-image';
import { parseMaipoPublicScene, publicSceneDiagnostics, sceneSupportDiagnostics, verifyPublicAssets, type PublicNode, type PublicAsset } from '@maipo/format';
import { DEFAULT_LIMITS, validateDimensions } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { webpDimensions } from '../codecs/webp-dimensions';

// Draft implementation limits, not a published profile or a browser heap cap.
export const DRAFT_IMAGE_LIMITS = Object.freeze({
  encodedBytes: 512 * 1024 * 1024,
  decodedBytes: DEFAULT_LIMITS.maxDecodedBytes,
  headerBytes: 1024 * 1024,
  chunks: 50_000,
});
const tag = (bytes: Uint8Array, offset: number) => String.fromCharCode(...bytes.subarray(offset, offset + 4));

/** Structural admission only; CRCs and compressed bitstreams remain the decoder's responsibility. */
export function draftImageDimensions(bytes: Uint8Array, mediaType: string): { width: number; height: number } {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const invalid = () => new Error(`Invalid or unsupported still-image container (${mediaType}).`);
  if (mediaType === 'image/png') {
    if (bytes.length < 33 || view.getUint32(0) !== 0x89504e47 || view.getUint32(4) !== 0x0d0a1a0a) throw invalid();
    let offset = 8; let count = 0; let hasData = false;
    while (offset < bytes.length) {
      if (++count > DRAFT_IMAGE_LIMITS.chunks || offset + 12 > bytes.length) throw invalid();
      const length = view.getUint32(offset); const type = tag(bytes, offset + 4);
      const end = offset + 12 + length;
      if (end > bytes.length || length > 0x7fffffff) throw invalid();
      if (count === 1 ? type !== 'IHDR' || length !== 13 : type === 'IHDR') throw invalid();
      if (['acTL', 'fcTL', 'fdAT'].includes(type)) throw new Error('Animated PNG is not admitted by the draft still-image loader.');
      if (type === 'IDAT') hasData = true;
      if (type === 'IEND') {
        if (length !== 0 || end !== bytes.length || !hasData) throw invalid();
        return { width: view.getUint32(16), height: view.getUint32(20) };
      }
      offset = end;
    }
    throw invalid();
  }
  if (mediaType === 'image/webp') {
    if (bytes.length < 20 || tag(bytes, 0) !== 'RIFF' || tag(bytes, 8) !== 'WEBP' || view.getUint32(4, true) + 8 !== bytes.length) throw invalid();
    let offset = 12; let count = 0; let bitstream: { width: number; height: number } | null = null;
    while (offset < bytes.length) {
      if (++count > DRAFT_IMAGE_LIMITS.chunks || offset + 8 > bytes.length) throw invalid();
      const type = tag(bytes, offset); const length = view.getUint32(offset + 4, true);
      const end = offset + 8 + length + (length & 1);
      if (end > bytes.length) throw invalid();
      if (type === 'ANIM' || type === 'ANMF' || (type === 'VP8X' && length > 0 && (bytes[offset + 8]! & 2))) {
        throw new Error('Animated WebP is not admitted by the draft still-image loader.');
      }
      if (type === 'VP8X' && (count !== 1 || length !== 10)) throw invalid();
      if (type === 'VP8 ' || type === 'VP8L') {
        if (bitstream) throw invalid();
        // Check the actual still-frame header too: a small VP8X canvas must not
        // hide a larger bitstream from the pre-allocation budget check.
        const prefix = new Uint8Array(12 + Math.min(18, end - offset));
        prefix.set(bytes.subarray(0, 12)); prefix.set(bytes.subarray(offset, offset + prefix.length - 12), 12);
        const fileSize = 12 + end - offset;
        new DataView(prefix.buffer).setUint32(4, fileSize - 8, true);
        bitstream = webpDimensions(prefix, fileSize);
      }
      offset = end;
    }
    if (!bitstream) throw invalid();
    const dimensions = webpDimensions(bytes.subarray(0, DRAFT_IMAGE_LIMITS.headerBytes), bytes.length);
    if (!dimensions || dimensions.width !== bitstream.width || dimensions.height !== bitstream.height) throw invalid();
    return dimensions;
  }
  if (mediaType === 'image/jpeg') {
    if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) throw invalid();
    const limit = Math.min(bytes.length, DRAFT_IMAGE_LIMITS.headerBytes);
    let offset = 2;
    while (offset < limit) {
      if (bytes[offset++] !== 0xff) throw invalid();
      while (offset < limit && bytes[offset] === 0xff) offset++;
      if (offset >= limit) break;
      const marker = bytes[offset++]!;
      if (marker === 0xd9 || marker === 0xda || marker === 0 || marker === 0xd8) throw invalid();
      if (marker === 1 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > limit) break;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > limit) throw invalid();
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker)) {
        if (length < 8 || length !== 8 + 3 * bytes[offset + 7]!) throw invalid();
        return { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3) };
      }
      offset += length;
    }
    throw new Error('JPEG dimensions were not found within the draft header budget.');
  }
  throw invalid();
}

/** Conservative base64 size accounting; never trusts asset.byteLength. */
export function assertDraftEncodedBudget(assets: readonly Pick<PublicAsset, 'data'>[]): void {
  let encoded = 0;
  for (const asset of assets) {
    encoded += Math.ceil(asset.data.length / 4) * 3;
    if (encoded > DRAFT_IMAGE_LIMITS.encodedBytes) throw new Error('Draft scene exceeds the aggregate encoded-resource budget.');
  }
}

export interface PreparedDraftImages {
  readonly images: ReadonlyMap<string, ImageBitmap>;
  dispose(): void;
}

/** Detached, owned image preparation. Does not admit a profile or activate an editor document. */
export async function prepareDraftSceneImages(value: unknown, signal?: AbortSignal, retainedDecodedBytes = 0): Promise<PreparedDraftImages> {
  throwIfAborted(signal);
  const scene = parseMaipoPublicScene(structuredClone(value));
  const errors = [...sceneSupportDiagnostics(scene), ...publicSceneDiagnostics(scene)].filter(item => item.severity === 'error');
  if (errors.length) throw new Error(errors.map(item => item.message).join(' '));
  // Count actual base64 storage before verification allocates decoded arrays. A
  // falsely small byteLength cannot bypass the aggregate encoded-resource guard.
  assertDraftEncodedBudget(scene.assets);
  const invalid = (await verifyPublicAssets(scene)).filter(item => item.severity === 'error');
  if (invalid.length) throw new Error(`Invalid scene assets: ${invalid.map(item => item.code).join(', ')}.`);
  throwIfAborted(signal);
  const needed = new Set<string>();
  const visit = (node: PublicNode): void => {
    if (node.type === 'group') node.children.forEach(visit);
    else if (node.type === 'image') needed.add(node.asset); // Hidden images have the same admission rules.
  };
  scene.children.forEach(visit);
  const unique = new Map<string, { blob: Blob; width: number; height: number; ids: string[] }>();
  if (!Number.isSafeInteger(retainedDecodedBytes) || retainedDecodedBytes < 0 || retainedDecodedBytes > DRAFT_IMAGE_LIMITS.decodedBytes) throw new Error('Invalid retained decoded-image budget.');
  let decodedBytes = retainedDecodedBytes;
  for (const asset of scene.assets) {
    if (!needed.has(asset.id)) continue;
    const key = `${asset.mediaType}:${asset.digest}`;
    const previous = unique.get(key);
    if (previous) { previous.ids.push(asset.id); continue; }
    const bytes = Uint8Array.from(atob(asset.data), character => character.charCodeAt(0));
    const dimensions = draftImageDimensions(bytes, asset.mediaType);
    validateDimensions(dimensions.width, dimensions.height);
    decodedBytes += dimensions.width * dimensions.height * 4;
    if (decodedBytes > DRAFT_IMAGE_LIMITS.decodedBytes) throw new Error('Draft scene exceeds the aggregate decoded-image budget.');
    unique.set(key, { blob: new Blob([bytes], { type: asset.mediaType }), ...dimensions, ids: [asset.id] });
  }
  // All headers and the aggregate budget pass before the first decoder call.
  const images = new Map<string, ImageBitmap>(); const owned = new Set<ImageBitmap>();
  const dispose = () => { for (const image of owned) image.close(); owned.clear(); images.clear(); };
  try {
    for (const resource of unique.values()) {
      throwIfAborted(signal);
      const bitmap = await decodeOrientedImage(resource.blob, { imageOrientation: 'from-image', colorSpaceConversion: 'default', premultiplyAlpha: 'none' }, signal);
      owned.add(bitmap);
      throwIfAborted(signal);
      validateDimensions(bitmap.width, bitmap.height);
      // Orientation can exchange axes; resizing or a different canvas is forbidden.
      if (!((bitmap.width === resource.width && bitmap.height === resource.height)
        || (bitmap.width === resource.height && bitmap.height === resource.width))) throw new Error('Decoded image dimensions disagree with the container header.');
      for (const id of resource.ids) images.set(id, bitmap);
    }
    return { images, dispose };
  } catch (error) { dispose(); throw error; }
}
